import { DefaultTheme } from 'styled-components';
import utils from './utils';

export interface ITheme {
    config: DefaultTheme | undefined;
    get: () => DefaultTheme;
    initTheme: (options?: ThemeConfigOptions) => DefaultTheme;
}

export type ThemeConfigOptions = {
    fontFamily?: string;
    linkColor: string;
    buttonColor: string;
    headerColor?: string;
    headerTextColor: string;
    headerUserBackground: string;
    headerUserTextColor: string;
    headerSearchBarTextColor: string;
    headerSearchBarBackgroundColor?: string;
    navBackground: string;
    navTextColor: string;
    bgColorNotLogged: string;
    forest: string;
};

export type Colors =
    | 'primary'
    | 'secondary'
    | 'headerColor'
    | 'headerTextColor'
    | 'headerUserBackground'
    | 'headerUserTextColor'
    | 'headerSearchBarTextColor'
    | 'headerSearchBarBackgroundColor'
    | 'navBackground'
    | 'navTextColor'
    | 'lavender'
    | 'orange'
    | 'yellow'
    | 'green'
    | 'navy'
    | 'overseas'
    | 'night1'
    | 'night2'
    | 'sandLight'
    | 'raspberry'
    | 'sky'
    | 'sky15'
    | 'sand'
    | 'forest'
    | 'grey0'
    | 'grey1'
    | 'grey2'
    | 'grey3'
    | 'grey4'
    | 'grey5'
    | 'grey6'
    | 'black'
    | 'black10'
    | 'black15'
    | 'black20'
    | 'white'
    | 'transparent';
type ThemeConfigColorType = { [color in Colors]: string };

export type Shades =
    | 'primaryL15'
    | 'primaryL80'
    | 'primaryL90'
    | 'primaryD20'
    | 'secondaryL15'
    | 'secondaryL80'
    | 'secondaryL90'
    | 'secondaryD20'
    | 'greenL20'
    | 'greenL80'
    | 'greenL90'
    | 'greenD20'
    | 'raspberryL95'
    | 'orangeL15'
    | 'orangeL80'
    | 'orangeL90'
    | 'orangeD20'
    | 'yellowL80'
    | 'yellowL90';
type ThemeConfigShadeType = { [color in Shades]: string };

export type ColorsOrShades = Colors | Shades;

export const NATIVE_COLORS = ['currentColor', 'inherit', 'transparent'] as const;
export type NativeColor = (typeof NATIVE_COLORS)[number];

export type FontWeight = {
    light: number;
    normal: number;
    medium: number;
    semiBold: number;
    bold: number;
};

export type ThemeType = {
    dpr: number;
    fallbackUrl: {
        article: string;
    };
    font: {
        family: string;
        color: string;
        hrefColor: string;
        size: string;
        lineHeight: string;
        weight: FontWeight;
    };
    color: ThemeConfigColorType & ThemeConfigShadeType;
    zIndex: {
        forceTop: number;
        tinyMCEModal: number;
        modal: number;
        dropdown: number;
        toaster: number;
        fullScreenContainer: number;
        tooltip: number;
        dropdownModal: number;
        dropDownModalIcon: number;
        sidePanelModal: number;
    };
    space: {
        xs: number;
        sm: number;
        md: number;
        l: number;
        xl: number;
    };
    size: {
        xs: number;
        sm: number;
        md: number;
        l: number;
        xl: number;
    };
};

export function getColor(theme: ThemeType, color: string) {
    return color in theme.color ? theme.color[color as Colors | Shades] : color;
}

export const defaultColors: ThemeConfigOptions = {
    linkColor: '#392994',
    buttonColor: '#7364E3',
    forest: '#0D846B',
    headerTextColor: '',
    headerUserBackground: '',
    headerUserTextColor: '',
    headerSearchBarTextColor: '#2f373f',
    navBackground: '#ffffff',
    navTextColor: '#4A4A52',
    bgColorNotLogged: '',
};

const Theme: ITheme = {
    config: undefined,
    get: function (): DefaultTheme {
        if (!this.config) {
            return this.initTheme();
        }
        return this.config;
    },
    initTheme: function (options = defaultColors) {
        const defaultColors: ThemeConfigColorType = {
            primary: options.buttonColor,
            secondary: options.linkColor,
            headerColor: (options && options.headerColor) || '',
            headerTextColor: options && options.headerTextColor,
            headerUserBackground: options && options.headerUserBackground,
            headerUserTextColor: options && options.headerUserTextColor,
            headerSearchBarTextColor: options.headerSearchBarTextColor,
            headerSearchBarBackgroundColor:
                options?.headerSearchBarBackgroundColor ??
                (options?.headerColor ? utils.colorContrast(options.headerColor as string) : '#313941'),
            navBackground: options && options.navBackground,
            navTextColor: options.navTextColor,

            lavender: '#8F7CEA',
            orange: '#E12B43',
            yellow: '#FFBB54',
            navy: '#131E4B',
            overseas: '#392994',
            green: '#0D846B',
            raspberry: '#E12B43',

            night1: '#2B3870',
            night2: '#10162D',

            sand: '#FFEED7',
            sandLight: '#FFF8F0',

            forest: options.forest,

            sky: '#9CC9FF',
            sky15: '#9CC9FF26',

            black: '#000000',
            black10: '#00000019',
            black15: '#00000026',
            black20: '#00000033',

            grey6: '#2E2E33',
            grey5: '#4A4A52',
            grey4: '#848491',
            grey3: '#BEBED1',
            grey2: '#DADAF0',
            grey1: '#EFEFFF',
            grey0: '#F8F8FF',
            white: '#FFFFFF',
            transparent: 'transparent',
        };

        const shades: ThemeConfigShadeType = {
            primaryD20: utils.getDarkShade(defaultColors.primary, 0.2),
            primaryL15: utils.getLightShade(defaultColors.primary, 0.15),
            primaryL80: utils.getLightShade(defaultColors.primary, 0.8),
            primaryL90: utils.getLightShade(defaultColors.primary, 0.9),

            secondaryD20: utils.getDarkShade(defaultColors.secondary, 0.2),
            secondaryL15: utils.getLightShade(defaultColors.secondary, 0.15),
            secondaryL80: utils.getLightShade(defaultColors.secondary, 0.8),
            secondaryL90: utils.getLightShade(defaultColors.secondary, 0.9),

            greenD20: utils.getDarkShade(defaultColors.green, 0.2),
            greenL20: utils.getLightShade(defaultColors.green, 0.2),
            greenL80: utils.getLightShade(defaultColors.green, 0.8),
            greenL90: utils.getLightShade(defaultColors.green, 0.9),

            orangeD20: utils.getDarkShade(defaultColors.orange, 0.2),
            orangeL15: utils.getLightShade(defaultColors.orange, 0.15),
            orangeL80: utils.getLightShade(defaultColors.orange, 0.8),
            orangeL90: utils.getLightShade(defaultColors.orange, 0.9),

            raspberryL95: utils.getLightShade(defaultColors.raspberry, 0.95),

            yellowL80: utils.getLightShade(defaultColors.yellow, 0.8),
            yellowL90: utils.getLightShade(defaultColors.yellow, 0.9),
        };

        this.config = {
            dpr: Math.ceil(window?.devicePixelRatio || 2),
            fallbackUrl: {
                article: '/imagestatic/30x30/icons/article.png',
            },
            font: {
                // Before using these properties, check JRCTypo.tsx
                family: options?.fontFamily ? `'${options.fontFamily}', sans-serif` : "'Poppins', sans-serif",
                color: defaultColors.grey5,
                hrefColor: defaultColors.secondary,
                size: '16px',
                lineHeight: '1.5em',
                weight: {
                    light: 300,
                    normal: 400,
                    medium: 500,
                    semiBold: 600,
                    bold: 700,
                },
            },
            color: {
                ...defaultColors,
                ...shades,
            },
            zIndex: {
                toaster: 1000000,
                forceTop: 100000,
                // Angular -- forceTop: 100000;
                // Angular -- tooltip: 50030;
                tooltip: 50030,
                tinyMCEModal: 50020,
                // ... modals, etc.
                // TinyMCE -- dialog: 50010;
                modal: 1100,
                // Angular -- modal: 1050;
                // Angular -- modalBackdrop: 1040;
                // Angular -- navbar: 1030;
                // Angular -- popover: 1010;
                // Angular -- dropdown: 1000;
                dropdown: 1000,
                sidePanelModal: 5,
                fullScreenContainer: 3,
                // Nav: 2
                dropdownModal: 10,
                dropDownModalIcon: 11,
            },
            space: {
                xs: 4,
                sm: 8,
                md: 16,
                l: 24,
                xl: 32,
            },
            size: {
                xs: 4,
                sm: 8,
                md: 16,
                l: 24,
                xl: 32,
            },
        };
        return this.config;
    },
};

export default Theme;
